import { Component, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService, PaymentV2Service, StorageService, UtilService, VariablesService, VwoService } from '../../shared/services';
import { IPaymentSipConfig, SIP_PAYMENT_DEFAULT_CONFIG } from './payment-sip.model';
import { GCaptchaComponent } from '../g-captcha/g-captcha.component';

@Component({
  selector: 'app-payment-sip',
  templateUrl: './payment-sip.component.html',
  styleUrls: ['./payment-sip.component.scss']
})
export class PaymentSipComponent implements OnInit, OnChanges, OnDestroy {

  @Input() config: IPaymentSipConfig = {};
  @Input() default: boolean;
  subs: Subscription[] = [];
  paymentData;
  oneTapOtpPopup: boolean;
  params: any;
  session_cart: any;

  @ViewChild(GCaptchaComponent, { static: true }) gCaptchaComponent: GCaptchaComponent;

  constructor(
    private api: ApiService,
    private paymentV2: PaymentV2Service,
    private util: UtilService,
    private actRoute: ActivatedRoute,
    private storage: StorageService,
    private vars: VariablesService,
    public vwo: VwoService
  ) { }

  ngOnChanges() { }

  ngOnInit() {
    // Set default config
    this.config = { ...SIP_PAYMENT_DEFAULT_CONFIG, ...this.config };
    this.subs.push(this.paymentV2.paymentConfig.subscribe((config) => {
      if (config) {
        this.config = { ...this.config, ...config };
      }
    }));
    this.checkCartSession();
    this.setPageToView();
    this.setPaymentCurrency();
    this.setPaymentTheme();
    this.subs.push(this.vars.userData.subscribe(res => {
      if (res) {
        this.paymentV2.paymentConfig.next({ user: res });
      } else {
        this.paymentV2.paymentConfig.next({ user: null, page: this.config.page || 'f' });
      }
    }));
    this.setSipCause();

    if (this.config.enable_multicause_slab) {
      this.paymentV2.userFormSubmit.subscribe(val => {
        if (val) {
          this.getPaymentOptions();
        }
      });
      this.paymentV2.oneTapSubmit.subscribe(data => {
        if (data) {
          this.intiatePayment(data);
        }
      });
    }
  }

  checkCartSession() {
    if (this.vars.isVariableLogin) {
      this.session_cart = this.config?.user?.id ? this.storage.getFromSession(`${this.config?.user?.id}SipCart`) : this.session_cart;
    } else {
      this.session_cart = this.storage.getFromSession('sip_cart');
    }
    const qParams = this.actRoute.snapshot.queryParams;
    if (qParams?.sip_wallet) {
      this.session_cart['cart']['donated_amount'] = +this.session_cart['cart']['donated_amount'] / (+qParams?.wallet_tenure || 6);
    }
    if (this.session_cart?.sip_cause_str) {
      this.session_cart.sip_cause_str = this.session_cart.sip_cause_str.split('/').join('');
    }
    if (this.session_cart?.timestamp) {
      const current_time = new Date().getTime();
      const time_difference = (current_time - this.session_cart.timestamp) / (1000 * 60); // difference in minutes
      if (time_difference > 15) {
        this.session_cart = null;
        this.storage.deleteFromSession('sip_cart');
      }
    }
  }

  setSipCause() {
    if (!this.config.sip_cause && (this.params?.pre_sip_cause || this.params?.sip_cause)) {
      this.config.sip_cause = this.util.stringToJson(this.params?.pre_sip_cause || this.params?.sip_cause);
      const total = this.paymentV2.totalSip(this.config.sip_cause, this.config);
    }
  }

  setPaymentCurrency() {
    const qParams = this.actRoute.snapshot.queryParams;
    if (qParams.abandon) {
      this.getPaymentDetails(qParams.currency);
    } else if (this.session_cart?.cart?.currency) {
      this.getPaymentDetails(this.session_cart.cart.currency);
    } else if (qParams.currency) {
      this.getPaymentDetails(qParams.currency);
    } else if (this.config.currency) {
      this.getPaymentDetails(this.config.currency);
    } else {
      this.util.currency.subscribe((currency) => currency && !this.config.currency ? this.getPaymentDetails(currency) : null);
    }
  }

  getPaymentDetails = (currency) => {
    let qParam = `?currency=${currency}&origin=${this.config.origin}&sip_cause=${this.paymentV2.totalSip(this.config.sip_cause, this.config)?.only_cause_list}`;
    if (this.util.utm_url_string) {
      qParam += `&${this.util.utm_url_string}`;
    }
    if (this.params?.skipmin || this.vars.selected_insurance) {
      qParam += `&skipmin=true`;
    }
    const url = environment.APP.API_URLS.GET_RECURRING_CONTRIBUTE_DETAILS + qParam;
    this.api.get(url).subscribe(res => {
      const details: any = res.data.data;
      if (typeof this.config?.sip_cause === 'string' && this.config?.sip_cause && details?.sip_cause) {
        this.paymentV2.paymentConfig.next({ sip_cause: details?.sip_cause });
      }
      if (this.util.utm_url_obj?.utm_source === 'internal_impact_calling' && currency === 'INR') {
        details.default_amount = 100;
        details.min_donation = details.min_donation > 50 ? 50 : details.min_donation;
      }
      if (this.params?.min_donation) {
        details.min_donation = +this.params?.min_donation;
      }
      details.selected_currency = currency;
      this.paymentV2.slabs = details?.slabs;
      this.paymentV2.paymentConfig.next({ campaign_id: details?.campaign_id, paymentDetails: details, currency: currency, fundraiser: details.campaign });
      this.setCartValues();
      if (this.config?.trialbased_donation) {
        this.applyTrialCoupon(details?.campaign?.id);
      }
    });
  }

  getPaymentOptions = () => {
    const recurring = this.config.sip_wallet ? null : 1;
    // const cartAmount = this.config?.amount * (this.util.getLengthToMultiply(this.config?.sip_cause));
    let url = environment.APP.API_URLS.GET_PAYMENT_OPTIONS + `?currency=${this.config.currency}&mobile_no=${this.config.user?.phone_1}&recurring=${recurring}&amount=${this.config?.amount}&is_mobile=${this.util.isMobile() ? 1 : 0}${this.util.utm_url_string ? '&' : ''}${this.util.utm_url_string}`;
    this.api.get(url).subscribe(async (res) => {
      this.config.paymentOptions = res.data;
      this.setFeeLableAmt();

      // Disable/Remove card from payment options
      // if (this.config.disableCard && recurring && this.config.currency === 'INR') {
      //   delete this.config.paymentOptions.options.CARD;
      // }

      // Unable last order only if user last order mode inlcud in option api
      try {
        await this.paymentV2.setUserLastOrder(this.config.user, this.config.paymentOptions.options);
      } catch (error) { }

      // Remove tabs if not present in option api
      if (recurring && this.config.paymentOptions?.options) {
        this.config.visibleTabs = this.config.optionTabs.filter(tab => Object.keys(this.config.paymentOptions.options).includes(tab.mode));
      } else {
        // to be removed once change payment migratartion done
        // tslint:disable-next-line: max-line-length
        this.config.visibleTabs = this.config.optionTabsOnetime.filter(tab => Object.keys(this.config.paymentOptions.options).includes(tab.mode));
      }

      // Set tab according to recomended tab
      const recommended = this.config.paymentOptions.options.RECOMMENDED.toLowerCase();
      this.config.selectedTab = this.config.visibleTabs.filter(tab => tab.mode.toLowerCase() === recommended)[0];
      this.config.recommended.label = this.config?.selectedTab?.label;
      this.config.visibleTabs.map(tab => {
        tab.tabs = Object.keys(this.config.paymentOptions.options).filter((item) => tab.tabs.includes(item));
        tab.recommended = tab.mode.toLowerCase() === recommended;
      });
      this.paymentV2.paymentConfig.next(this.config);
    });
  }

  setFeeLableAmt() {
    if (this.config?.enableMaintenanceFee && this.config.page === 'op') {
      this.paymentV2.paymentConfig.next({ labelAmount: (this.config?.cart_amount || this.config.amount) + this.config.paymentDetails.mandate_fee });
    } else {
      this.paymentV2.paymentConfig.next({ labelAmount: null });
    }
  }

  setCartValues() {
    let update_config: any = { amount: this.config.amount };
    if (!this.session_cart && this.config.page === 'op') {
      this.util.router.navigate([], { queryParams: { page: null }, queryParamsHandling: 'merge' });
    }
    const qParams = this.actRoute.snapshot.queryParams;
    const sip_cause_str = this.util.jsonToString(this.config.sip_cause);
    if (this.session_cart?.cart && sip_cause_str === this.session_cart?.sip_cause_str && qParams.st === 'f') {
      update_config = {
        amount: this.session_cart.cart.donated_amount,
        cart: this.session_cart.cart,
        fundraiser: this.session_cart.campaign
      }
    } else if (this.config.minAmount) {
      update_config.amount = this.config.paymentDetails.min_donation;
    } else if (qParams.abandon) {
      update_config.amount = +qParams.abandon;
    } else if (qParams.amt) {
      update_config.amount = +qParams.amt;
    } else if (!this.config.amount && this.config?.paymentDetails?.default_amount) {
      update_config.amount = this.config.paymentDetails.default_amount;
    }
    this.config.campaign_id = +this.config?.campaign_id || this.config?.paymentDetails?.campaign?.id;
    if (sip_cause_str === this.session_cart?.sip_cause_str && this.config.currency === this.session_cart?.cart?.currency &&
      ((this.config?.campaign_id === this.session_cart?.cart?.campaign_id) || this.config?.sip_story)
    ) {
      update_config = {
        amount: this.session_cart.cart.donated_amount,
        cart: this.session_cart.cart,
        fundraiser: this.session_cart.campaign
      };
    }
    this.paymentV2.paymentConfig.next(update_config);
    this.getPaymentOptions();
  }

  setPaymentTheme() {
    if (this.util.isBrowser && this.config.themeColor) {
      const code = this.util.hexToRgb(this.config.themeColor);
      this.util.doc.documentElement.style.setProperty('--payment-sip-color', this.config.themeColor);
      this.util.doc.documentElement.style.setProperty('--payment-sip-color-bg', `${code.r}, ${code.g}, ${code.b}`);
    }
  }

  setPageToView() {
    this.actRoute.queryParams.subscribe(params => {
      if (!this.config.page || this.params?.page !== params?.page) {
        this.paymentV2.paymentConfig.next({ page: params?.page || 'f' });
      }
      this.params = params;
    });
  }

  async intiatePayment(data) {
    this.checkCartSession();
    if (typeof this.config.sip_cause === 'string') {
      this.config.sip_cause = [{ sip_cause: this.config.sip_cause, amount: this.config.amount }];
    } else if (!this.config.sip_cause) {
      this.config.sip_cause = [{ sip_cause: this.vars.default_sip_cause, amount: this.config.amount }];
    }
    const total_sip = this.paymentV2.totalSip(this.config.sip_cause, this.config);
    if (!total_sip.is_valid) { return; }
    this.paymentV2.isOrderLoading = true;
    this.sendLead();
    let transaction_type = '';
    const sip_cause_str = this.util.jsonToString(this.config.sip_cause);
    const cart_amount = total_sip?.total_amount || this.config.amount;
    if (!this.config.cart || (sip_cause_str !== this.session_cart?.sip_cause_str) || (this.config.campaign_id !== this.session_cart?.cart?.campaign_id) || (this.config.cart.donated_amount !== cart_amount)) {
      if (this.config.currency !== 'INR' && !this.paymentV2.captchaResponse && !this.paymentV2.otpVerified) {
        this.paymentData = data;
        // tslint:disable-next-line: max-line-length
        if (this.config.show_one_tap_otp && this.config.one_tap_pay && !this.oneTapOtpPopup && this.config?.user?.lastorder?.payment_gateway?.match('stripe')) {
          this.oneTapOtpPopup = true;
        } else {
          this.gCaptchaComponent.executeCaptcha();
        }
        this.paymentV2.isOrderLoading = false;
        return false;
      }
      let skipmin = false;
      if (cart_amount >= 1) {
        skipmin = true
      }
      const cartPayload = {
        campaign_id: this.config.campaign_id,
        amount: cart_amount,
        currency: this.config.paymentDetails.selected_currency,
        is_anonymous: this.config.paymentDetails.is_anonymous,
        pledge_more: this.config.pledge_more,
        sip_cause: this.config.sip_cause,
        only_cause_list: total_sip.only_cause_list,
        multicause: this.config.enable_multicause_slection || null,
        sip_wallet: this.config.sip_wallet,
        wallet_tenure: this.config.wallet_tenure,
        auto_pay_m1: this.config.auto_pay_m1,
        wallet_to_autopay: this.config.wallet_to_autopay,
        wallet_recharge: this.config.wallet_recharge,
        sip_story: this.config.sip_story,
        skipmin: skipmin,
        recurring: !this.config.wallet_recharge ? 1 : null
      };

      if (this.config.sip_story) {
        transaction_type = '';
      } else if (this.config.sip_wallet) {
        transaction_type = `wallet_new_sip_${this.config.wallet_tenure}`;
      } else if (this.config.auto_pay_m1) {
        transaction_type = 'new_sip_amt_1';
      }
      cartPayload['transaction_type'] = transaction_type;

      const cart = await this.paymentV2.createCart(cartPayload);
      this.config.cart = cart?.cart;
      this.paymentV2.paymentConfig.next({ cart: cart.cart, fundraiser: cart.fundraiser });
    }

    Object.assign(data, {
      ct: this.config.fundraiser.custom_tag,
      origin: this.config.origin,
      sip: true,
      options: this.config.paymentOptions,
      fundraiser: this.config.fundraiser,
      cart: this.config.cart,
      stripeSwitch: data?.mode === 'CARD',
      stripeIndiaOnly: this.config.currency === 'INR' ? true : false,
      user: this.config.user,
      month_interval: this.config.month_interval,
      sip_wallet: this.config.sip_wallet,
      wallet_tenure: this.config.sip_wallet ? this.config.wallet_tenure : null,
      auto_pay_m1: this.config.auto_pay_m1,
      subscription_id: this.config.subscription_id || null,
      payment_method_change: this.config.payment_method_change || null,
      pledge_more: this.config.pledge_more,
      sip_cause: this.config?.sip_cause,
      only_cause_list: total_sip.only_cause_list,
      multicause: this.config.enable_multicause_slection || null,
      wallet_to_autopay: this.config.wallet_to_autopay,
      sip_story: this.config.sip_story,
      tenure: this.config.tenure,
      transaction_type: transaction_type,
      frequency_term: this.config.frequency_term,
      frequency: this.config.frequency,
      ngo_sip: this.config.ngo_sip,
      plan: this.config.plan
    });
    await this.paymentV2.createOrder(data);
  }

  sendLead() {
    const payload = {
      email: this.config.user.email,
      phone: this.config.user.phone_1,
      created_date: new Date(),
      name: this.config.user.full_name,
      lead_name: this.config.user.full_name,
      frequency: this.config.user.aggdonationall?.funds,
      final_url: this.vars.pageName,
      source: this.vars.pageName,
      page: this.vars.pageName,
      extension: this.config.user.extension,
      type: 'ketto-campaign-lead',
      mail_to: this.vars.leadMailTo,
      subject: 'Netbanking SIP Signup Started'
    };
    this.api.post(environment.APP.API_URLS.GET_LEAD, payload, 'false').subscribe(_ => _);
  }

  captchaResponse(e) {
    this.paymentV2.captchaResponse = e;
    this.intiatePayment(this.paymentData);
  }

  /** OTP varification for one tap payment */
  otpVerified = (isVarified) => {
    this.oneTapOtpPopup = isVarified;
    if (isVarified) {
      this.paymentV2.otpVerified = isVarified;
      this.intiatePayment(this.paymentData);
    }
  }

  applyTrialCoupon(campaign_id) {
    this.api.applyCoupon(campaign_id, { coupon_id: 125 }).subscribe(() => { });
  }


  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

}
